import type { FormatDistanceToNowStrictOptions } from "date-fns";

import { type ClassValue, clsx } from "clsx";
import { formatDistanceToNowStrict } from "date-fns";
// import crypto from "node:crypto";
import { twMerge } from "tailwind-merge";

import type { T_PaginationParams, T_SortParams } from "@/types/filters";

import { DispatchStatusEnum } from "@/types/order";

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

export const STATUS_COLORS = {
	pending: "bg-yellow-500",
	cancelled: "bg-green-500",
	completed: "bg-red-500"
};

export function formatDate(date: string | number | Date, options: Intl.DateTimeFormatOptions = {
	year: "numeric",
	month: "short",
	day: "numeric"
}, lang: Intl.LocalesArgument = "en-GB") {
	return new Date(
		date
	).toLocaleDateString(lang, options);
}

export function formatCurrency(value: number, currency: string = "USD") {
	return new Intl.NumberFormat("en-US", {
		style: "currency",
		currency,
		currencyDisplay: "narrowSymbol"
	}).format(value);
}

export function formatKebabCase(str: string) {
	return str.replace(/-/g, " ").replace(/_/g, " ").toLowerCase();
}

export function formatRelativeDate(date: Date | number | string, options: FormatDistanceToNowStrictOptions = { addSuffix: true }) {
	return formatDistanceToNowStrict(new Date(date), options);
}

export function formatIntlRelativeDate(date: Date): string {
	const rtf = new Intl.RelativeTimeFormat("en", { style: "short" });
	const dateFormatter = new Intl.DateTimeFormat("en", {
		year: "numeric",
		month: "short",
		day: "numeric"
	});
	const now = new Date();
	const diffInSeconds = Math.round((date.getTime() - now.getTime()) / 1000);

	const units: { unit: Intl.RelativeTimeFormatUnit; seconds: number }[] = [
		{ unit: "year", seconds: 31536000 },
		{ unit: "month", seconds: 2592000 },
		{ unit: "week", seconds: 604800 },
		{ unit: "day", seconds: 86400 },
		{ unit: "hour", seconds: 3600 },
		{ unit: "minute", seconds: 60 },
		{ unit: "second", seconds: 1 }
	];

	for (const { unit, seconds } of units) {
		const diff = Math.floor(diffInSeconds / seconds);
		if (Math.abs(diff) >= 1) {
			return rtf.format(diff, unit);
		}
	}

	return dateFormatter.format(date);
}

export function sanitizeParams<T extends Record<string, unknown> & T_PaginationParams & T_SortParams>(search: T) {
	const DEFAULT_PAGE_INDEX = 0;
	const DEFAULT_PAGE_SIZE = 10;

	const newSearch = { ...search };
	Object.keys(newSearch).forEach((key) => {
		const value = newSearch[key];
		if (
			value === undefined
			|| value === ""
			|| (typeof value === "number" && isNaN(value))
		) {
			delete newSearch[key];
		}
	});

	if (search.pageIndex === DEFAULT_PAGE_INDEX)
		delete newSearch.pageIndex;

	if (search.pageSize === DEFAULT_PAGE_SIZE)
		delete newSearch.pageSize;

	return newSearch;
}

export function genOrderProgressVal(status: DispatchStatusEnum) {
	switch (status) {
		case DispatchStatusEnum.Delivered:
			return 100;
		case DispatchStatusEnum.Arrived:
			return 80;
		case DispatchStatusEnum.InTransit:
			return 60;
		case DispatchStatusEnum.Dispatch:
			return 40;
		case DispatchStatusEnum.Pending:
			return 20;
		default:
			return 0;
	}
}

export function truncateText(text: string, maxLength = 30) {
	if (!text || text.length <= maxLength)
		return text;
	return `${text.slice(0, maxLength)}...`;
}
